<template>
  <div class="w-full bg-neutral-100 flex flex-row">
    <div class="flex flex-col h-full w-full gap-4 px-5 py-4">
      <contacts-header
        :search-query="searchQuery"
        :header-title="pageTitle"
        :segments-id="segmentsId"
        @on-input-search="onInputSearch"
        @on-toggle-create="onToggleCreate"
        @on-toggle-filter="onToggleFilters"
        @on-search-submit="onSearchSubmit"
        @on-toggle-import="onToggleImport"
      />
      <contacts-table
        :contacts="records"
        :search-query="searchQuery"
        :show-search-empty-state="showEmptySearchResult"
        :is-loading="uiFlags.isFetching"
        :on-click-contact="openContactInfoPanel"
        :current-page="currentPage"
        :total-pages="totalPages"
        :total-items="meta.count || 0"
        :page-size="dynamicPageSize"
        @on-sort-change="onSortChange"
        @on-column-search="onColumnSearch"
        @on-column-filter="onColumnFilterChange"
        @page-change="onPageChange"
      />
    </div>

    <create-contact :show="showCreateModal" @cancel="onToggleCreate" />
    <woot-modal
      :show.sync="showImportModal"
      :on-close="onToggleImport"
      class="contacts-import-modal"
    >
      <import-contacts v-if="showImportModal" :on-close="onToggleImport" />
    </woot-modal>
    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="closeAdvanceFiltersModal"
      size="medium"
      class="contact-advanced-filter-modal"
    >
      <contacts-advanced-filters
        v-if="showFiltersModal"
        :on-close="closeAdvanceFiltersModal"
        :initial-filter-types="contactFilterItems"
        :initial-applied-filters="appliedFilter"
        :active-segment-name="activeSegmentName"
        :is-segments-view="hasActiveSegments"
        @applyFilter="onApplyFilter"
        @updateSegment="onUpdateSegment"
        @clearFilters="clearFilters"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ContactsHeader from './Header.vue';
import ContactsTable from './ContactsTable.vue';
import CreateContact from 'dashboard/routes/dashboard/conversation/contact/CreateContact.vue';
import ImportContacts from './ImportContacts.vue';
import ContactsAdvancedFilters from './ContactsAdvancedFilters.vue';
import contactFilterItems from '../contactFilterItems';
import filterQueryGenerator from '../../../../helper/filterQueryGenerator';
import { CONTACTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import responsiveTablePageSize from 'v2/mixins/responsiveTablePageSize';
import countries from 'shared/constants/countries.js';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';

const DEFAULT_PAGE = 1;

export default {
  components: {
    ContactsHeader,
    ContactsTable,
    CreateContact,
    ImportContacts,
    ContactsAdvancedFilters,
  },
  mixins: [responsiveTablePageSize],
  props: {
    label: { type: String, default: '' },
    segmentsId: {
      type: [String, Number],
      default: 0,
    },
  },
  data() {
    return {
      showCreateModal: false,
      showImportModal: false,
      sortConfig: { last_activity_at: 'desc' },
      showFiltersModal: false,
      contactFilterItems: contactFilterItems.map(filter => ({
        ...filter,
        attributeName: this.$t(
          `CONTACTS_FILTER.ATTRIBUTES.${filter.attributeI18nKey}`
        ),
      })),
      segmentsQuery: {},
      appliedFilter: [],
      columnFilterItems: [],
    };
  },
  computed: {
    ...mapGetters({
      records: 'contacts/getContacts',
      uiFlags: 'contacts/getUIFlags',
      meta: 'contacts/getMeta',
      segments: 'customViews/getCustomViews',
      getAppliedContactFilters: 'contacts/getAppliedContactFilters',
    }),
    searchQuery: {
      set(data) {
        return this.$store.commit('contacts/SET_SEARCH_QUERY', data);
      },
      get() {
        return this.$store.getters['contacts/getSearchQuery'];
      },
    },
    showEmptySearchResult() {
      const hasEmptyResults = !!this.searchQuery && this.records.length === 0;
      return hasEmptyResults;
    },
    hasAppliedFilters() {
      return this.getAppliedContactFilters.length;
    },
    hasActiveSegments() {
      return this.activeSegment && this.segmentsId !== 0;
    },
    isSegmentRoute() {
      return Boolean(this.segmentsId);
    },
    hasAdvancedFilterQuery() {
      return (
        Array.isArray(this.segmentsQuery?.payload) &&
        this.segmentsQuery.payload.length > 0
      );
    },
    pageTitle() {
      if (this.hasActiveSegments) {
        return this.activeSegment.name;
      }
      if (this.label) {
        return `#${this.label}`;
      }
      return this.$t('CONTACTS_PAGE.HEADER');
    },
    pageParameter() {
      const selectedPageNumber = Number(this.$route.query?.page);
      return !Number.isNaN(selectedPageNumber) &&
        selectedPageNumber >= DEFAULT_PAGE
        ? selectedPageNumber
        : DEFAULT_PAGE;
    },
    currentPage() {
      return Number(this.meta.currentPage) || DEFAULT_PAGE;
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil((this.meta.count || 0) / this.dynamicPageSize)
      );
    },
    activeSegment() {
      if (this.segmentsId) {
        const [firstValue] = this.segments.filter(
          view => view.id === Number(this.segmentsId)
        );
        return firstValue;
      }
      return undefined;
    },
    activeSegmentName() {
      return this.activeSegment?.name;
    },
    baseFilterItems() {
      if (this.hasActiveSegments) {
        return this.activeSegment.query?.payload || [];
      }
      if (this.hasAdvancedFilterQuery) {
        return this.segmentsQuery.payload;
      }
      return [];
    },
    labelFilterItems() {
      return this.label
        ? [this.columnFilterItem('labels', 'equal_to', [this.label])]
        : [];
    },
    usesFilterEndpoint() {
      return (
        this.hasActiveSegments ||
        this.hasAdvancedFilterQuery ||
        this.columnFilterItems.length > 0
      );
    },
  },
  watch: {
    'records.length': function onContactsLengthChange(len) {
      if (len > 0) {
        this.syncTablePageSize(() => this.refetchCurrentContacts());
      }
    },
    label() {
      if (this.hasAdvancedFilterQuery) {
        this.clearFilters();
      } else {
        this.fetchContacts(DEFAULT_PAGE);
      }
    },
    activeSegment(segment, previous) {
      if (
        segment?.id === previous?.id &&
        JSON.stringify(segment?.query) === JSON.stringify(previous?.query)
      ) {
        return;
      }
      this.fetchContacts(DEFAULT_PAGE);
    },
  },
  mounted() {
    if (this.hasAppliedFilters) {
      this.segmentsQuery = filterQueryGenerator(this.getAppliedContactFilters);
    }
    this.fetchContacts(this.pageParameter);
  },
  methods: {
    updatePageParam(page) {
      window.history.pushState({}, null, `${this.$route.path}?page=${page}`);
    },
    getSortAttribute() {
      let sortAttr = Object.keys(this.sortConfig).reduce((acc, sortKey) => {
        const sortOrder = this.sortConfig[sortKey];
        if (sortOrder) {
          const sortOrderSign = sortOrder === 'asc' ? '' : '-';
          return `${sortOrderSign}${sortKey}`;
        }
        return acc;
      }, '');
      if (!sortAttr) {
        this.sortConfig = { last_activity_at: 'desc' };
        sortAttr = '-last_activity_at';
      }
      return sortAttr;
    },
    fetchContacts(page) {
      if (this.isSegmentRoute && !this.activeSegment) return;
      this.updatePageParam(page);
      const sortAttr = this.getSortAttribute();
      const perPage = this.dynamicPageSize;
      if (this.usesFilterEndpoint) {
        this.$store.dispatch('contacts/filter', {
          queryPayload: this.buildFilterPayload(),
          page,
          sortAttr,
          perPage,
        });
        return;
      }
      const requestParams = { page, sortAttr, label: this.label, perPage };
      const search =
        this.searchQuery.charAt(0) === '+'
          ? this.searchQuery.substring(1)
          : this.searchQuery;
      if (search) {
        this.$store.dispatch('contacts/search', {
          search,
          ...requestParams,
        });
      } else {
        this.$store.dispatch('contacts/get', requestParams);
      }
    },
    buildFilterPayload() {
      const groups = [
        this.baseFilterItems,
        this.columnFilterItems,
        this.labelFilterItems,
      ].filter(group => group.length);
      const payload = groups.reduce((acc, group, groupIndex) => {
        const isLastGroup = groupIndex === groups.length - 1;
        return acc.concat(
          group.map((item, itemIndex) => {
            if (itemIndex < group.length - 1) return { ...item };
            return { ...item, query_operator: isLastGroup ? undefined : 'and' };
          })
        );
      }, []);
      return { payload };
    },

    onInputSearch(event) {
      const newQuery = event.target.value;
      const refetchAllContacts = !!this.searchQuery && newQuery === '';
      this.searchQuery = newQuery;
      if (refetchAllContacts) {
        this.fetchContacts(DEFAULT_PAGE);
      }
    },
    onSearchSubmit() {
      if (this.searchQuery) {
        this.fetchContacts(DEFAULT_PAGE);
      }
    },
    onColumnSearch(term) {
      this.columnFilterItems = [];
      this.searchQuery = (term || '').trim();
      this.fetchContacts(DEFAULT_PAGE);
    },
    onColumnFilterChange(filters) {
      this.columnFilterItems = (filters || []).reduce(
        (acc, f) => acc.concat(this.buildColumnFilterItems(f)),
        []
      );
      if (this.columnFilterItems.length) {
        this.searchQuery = '';
      }
      this.fetchContacts(DEFAULT_PAGE);
    },
    buildColumnFilterItems({ key, presence = 'all', value = '' } = {}) {
      if (key !== 'phone_number' && key !== 'email') return [];
      const items = [];
      if (presence === 'has') {
        items.push(this.columnFilterItem(key, 'is_present'));
      } else if (presence === 'none') {
        items.push(this.columnFilterItem(key, 'is_not_present'));
      }
      if (value) {
        items.push(this.columnFilterItem(key, 'contains', [value]));
      }
      return items;
    },
    columnFilterItem(key, operator, values = []) {
      return {
        attribute_key: key,
        filter_operator: operator,
        values,
        query_operator: 'and',
        attribute_model: 'standard',
      };
    },
    onPageChange(page) {
      this.fetchContacts(page);
    },
    refetchCurrentContacts() {
      this.fetchContacts(this.currentPage);
    },
    openContactInfoPanel(contactId) {
      this.$router.push({
        name: 'contact_profile_dashboard',
        params: {
          accountId: this.$route.params.accountId,
          contactId,
        },
      });
    },
    onToggleCreate() {
      this.showCreateModal = !this.showCreateModal;
    },
    onToggleImport() {
      this.showImportModal = !this.showImportModal;
    },
    onSortChange(params) {
      this.sortConfig = params;
      this.fetchContacts(this.currentPage);

      const sortBy =
        Object.entries(params).find(pair => Boolean(pair[1])) || [];

      this.$track(CONTACTS_EVENTS.APPLY_SORT, {
        appliedOn: sortBy[0],
        order: sortBy[1],
      });
    },
    onToggleFilters() {
      if (this.hasActiveSegments) {
        this.initializeSegmentToFilterModal(this.activeSegment);
      }
      this.showFiltersModal = true;
    },
    closeAdvanceFiltersModal() {
      this.showFiltersModal = false;
      this.appliedFilter = [];
    },
    onApplyFilter(payload) {
      if (!payload || payload.length === 0) {
        this.clearFilters();
        this.showFiltersModal = false;
        return;
      }
      this.segmentsQuery = filterQueryGenerator(payload);
      this.fetchContacts(DEFAULT_PAGE);
      this.showFiltersModal = false;
    },
    onUpdateSegment(payload, segmentName) {
      const payloadData = {
        ...this.activeSegment,
        name: segmentName,
        query: filterQueryGenerator(payload),
      };
      this.$store.dispatch('customViews/update', payloadData);
      this.closeAdvanceFiltersModal();
    },
    clearFilters() {
      this.$store.dispatch('contacts/clearContactFilters');
      this.segmentsQuery = {};
      this.fetchContacts(DEFAULT_PAGE);
    },
    setParamsForEditSegmentModal() {
      // Here we are setting the params for edit segment modal to show the existing values.

      // For custom attributes we get only attribute key.
      // So we are mapping it to find the input type of the attribute to show in the edit segment modal.
      const params = {
        countries: countries,
        filterTypes: contactFilterItems,
        allCustomAttributes:
          this.$store.getters['attributes/getAttributesByModel'](
            'contact_attribute'
          ),
      };
      return params;
    },
    initializeSegmentToFilterModal(activeSegment) {
      // Here we are setting the params for edit segment modal.
      //  To show the existing values. when we click on edit segment button.

      // Here we get the query from the active segment.
      // And we are mapping the query to the actual values.
      // To show in the edit segment modal by the help of generateValuesForEditCustomViews helper.
      const query = activeSegment?.query?.payload;
      if (!Array.isArray(query)) return;

      this.appliedFilter.push(
        ...query.map(filter => ({
          attribute_key: filter.attribute_key,
          attribute_model: filter.attribute_model,
          filter_operator: filter.filter_operator,
          values: Array.isArray(filter.values)
            ? generateValuesForEditCustomViews(
                filter,
                this.setParamsForEditSegmentModal()
              )
            : [],
          query_operator: filter.query_operator,
          custom_attribute_type: filter.custom_attribute_type,
        }))
      );
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .contacts-import-modal .modal-container {
  width: 700px;
  max-width: calc(100vw - 32px);
}
</style>
